import React, { useEffect, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  Alert,
  Platform,
  ScrollView,
  StyleSheet,
  Switch,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import PickerField from '../components/PickerField';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { collection, doc, serverTimestamp, Timestamp, writeBatch } from 'firebase/firestore';
import { Colors, GlobalStyles, Radius, Spacing, Typography } from '../theme';
import type { RootStackParamList } from '../types';
import TonyteBackground from '../components/TonyteBackground';
import { auth, FIRESTORE_COLLECTIONS, firestore } from '../firebase';
import { requestNotificationPermission } from '../utils/permissions';
import { findSupportedCity, getDefaultVisibleCity, getVisibleCities, normalizeCityKey } from '../utils/cities';
import { useProfile } from '../hooks/useProfile';
import { useI18n } from '../i18n';
import { requireVerifiedEmailForAction } from '../utils/verificationGate';
import { useTheme } from '../theme/useTheme';
import { daylightStyle } from '../theme/daylightStyle';
import type { TonyteTheme } from '../theme/themes';

type Props = NativeStackScreenProps<RootStackParamList, 'UploadPlan' | 'StudiZoneCreatePlan' | 'StudiZoneCreatePlanScreen'>;

type MeetingType = 'public_place' | 'private_place' | 'unknown';
type Visibility = 'public' | 'friends' | 'invite_only';

function getTodayMidnight(): Date {
  const d = new Date(); d.setHours(0, 0, 0, 0); return d;
}
function getTodayStr(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
// 'YYYY-MM-DD' + 'HH:MM' → local Date (null if either is missing/invalid).
function localDateTime(date: string, time: string): Date | null {
  const d = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date.trim());
  const t = /^(\d{1,2}):(\d{2})/.exec(time.trim());
  if (!d || !t) return null;
  const value = new Date(Number(d[1]), Number(d[2]) - 1, Number(d[3]), Number(t[1]), Number(t[2]), 0, 0);
  return Number.isNaN(value.getTime()) ? null : value;
}
const pad2 = (value: number) => String(value).padStart(2, '0');
const toDateStr = (value: Date) => `${value.getFullYear()}-${pad2(value.getMonth() + 1)}-${pad2(value.getDate())}`;
const toTimeStr = (value: Date) => `${pad2(value.getHours())}:${pad2(value.getMinutes())}`;
// A plan's end is suggested 3 hours after its start and may be at most 14
// days after it, so no plan stays listed for weeks.
const DEFAULT_PLAN_LENGTH_MS = 3 * 60 * 60 * 1000;
const MAX_PLAN_LENGTH_MS = 14 * 24 * 60 * 60 * 1000;

function getNowTimeStr(): string {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

const MEETING_TYPES: Array<{ value: MeetingType; labelKey: string }> = [
  { value: 'public_place', labelKey: 'uploadPlan.publicPlace' },
  { value: 'private_place', labelKey: 'uploadPlan.privatePlace' },
  { value: 'unknown', labelKey: 'uploadPlan.unknown' },
];

const VISIBILITY_OPTIONS: Array<{ value: Visibility; labelKey: string }> = [
  { value: 'public', labelKey: 'uploadPlan.public' },
  { value: 'friends', labelKey: 'uploadPlan.friends' },
  { value: 'invite_only', labelKey: 'uploadPlan.inviteOnly' },
];

const UploadPlanScreen: React.FC<Props> = ({ navigation, route }) => {
  const { t } = useI18n();
  const { theme } = useTheme();
  const appearance = useMemo(() => createThemeStyles(theme), [theme]);
  const insets = useSafeAreaInsets();
  const currentUid = auth.currentUser?.uid || null;
  const { profile } = useProfile(currentUid);
  const visibleCities = useMemo(() => getVisibleCities(profile?.role), [profile?.role]);
  const defaultVisibleCity = useMemo(() => getDefaultVisibleCity(profile?.role), [profile?.role]);
  // Optional pre-fill from a Plan Template (PlanTemplateCarousel on the
  // Community Plans screen) — every value below stays a normal editable
  // form field from here on. Date/time are deliberately never seeded: the
  // person creating the plan decides when it happens. This flag is only
  // used for the small "Started from a Tonyte template" hint further down
  // and is never written to the plan document.
  const templateValues = route.params?.initialValues;
  const [title, setTitle] = useState(templateValues?.title ?? '');
  const [description, setDescription] = useState(templateValues?.description ?? '');
  const [city, setCity] = useState(defaultVisibleCity.label);
  const [searchLocation, setSearchLocation] = useState(templateValues?.location ?? '');
  const [startDate, setStartDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endDate, setEndDate] = useState('');
  const [endTime, setEndTime] = useState('');
  const [endTouched, setEndTouched] = useState(false);
  const [meetingType, setMeetingType] = useState<MeetingType>('public_place');
  const [visibility, setVisibility] = useState<Visibility>('public');
  const [planMaxParticipants, setPlanMaxParticipants] = useState(
    templateValues?.participantLimit ? String(templateValues.participantLimit) : '',
  );
  const [safetyAcknowledged, setSafetyAcknowledged] = useState(false);
  const [saving, setSaving] = useState(false);
  const isStudiZonePlan = route.name === 'StudiZoneCreatePlan' || route.name === 'StudiZoneCreatePlanScreen';

  useEffect(() => {
    if (currentUid && !profile) return;
    const selected = findSupportedCity(normalizeCityKey(city));
    if (selected && visibleCities.some(item => item.value === selected.value)) return;
    setCity(defaultVisibleCity.label);
  }, [city, currentUid, defaultVisibleCity.label, profile, visibleCities]);

  // Until the user picks an end themselves, keep it 3 hours after the start.
  useEffect(() => {
    if (endTouched) return;
    const start = localDateTime(startDate, startTime);
    if (!start) return;
    const suggested = new Date(start.getTime() + DEFAULT_PLAN_LENGTH_MS);
    setEndDate(toDateStr(suggested));
    setEndTime(toTimeStr(suggested));
  }, [endTouched, startDate, startTime]);

  const save = async () => {
    const user = auth.currentUser;
    if (!user) {
      Alert.alert(t('auth.signInRequired'), t('uploadPlan.signInBody'));
      return;
    }
    if (!requireVerifiedEmailForAction()) return;
    if (!title.trim() || !description.trim()) {
      Alert.alert(t('uploadPlan.missingDetails'), t('uploadPlan.missingDetailsBody'));
      return;
    }
    if (!searchLocation.trim() || !city.trim()) {
      Alert.alert(t('uploadPlan.missingLocation'), t('uploadPlan.missingLocationBody'));
      return;
    }
    if (!startDate.trim() || !startTime.trim()) {
      Alert.alert(t('uploadPlan.missingDate'), t('uploadPlan.missingDateBody'));
      return;
    }
    if (planMaxParticipants.trim() && Number(planMaxParticipants) < 2) {
      Alert.alert(t('uploadPlan.participantLimit'), t('uploadPlan.participantLimitBody'));
      return;
    }
    if (!safetyAcknowledged) {
      Alert.alert(t('uploadPlan.safetySetup'), t('uploadPlan.safetySetupBody'));
      return;
    }

    const todayS = getTodayStr();
    if (startDate < todayS) {
      Alert.alert(t('uploadPlan.invalidDate'), t('uploadPlan.futureDate'));
      return;
    }
    if (startDate === todayS && startTime < getNowTimeStr()) {
      Alert.alert(t('uploadPlan.invalidTime'), t('uploadPlan.pastTime'));
      return;
    }
    const startsAt = localDateTime(startDate, startTime);
    const endsAt = localDateTime(endDate, endTime);
    if (!endsAt) {
      Alert.alert(t('uploadPlan.missingEnd'), t('uploadPlan.missingEndBody'));
      return;
    }
    if (startsAt && endsAt.getTime() <= startsAt.getTime()) {
      Alert.alert(t('uploadPlan.invalidTime'), t('uploadPlan.endBeforeStart'));
      return;
    }
    if (startsAt && endsAt.getTime() - startsAt.getTime() > MAX_PLAN_LENGTH_MS) {
      Alert.alert(t('uploadPlan.invalidDate'), t('uploadPlan.endTooLate'));
      return;
    }

    setSaving(true);
    try {
      const selectedCity = visibleCities.find(item => item.value === normalizeCityKey(city)) || defaultVisibleCity;
      const cityKey = selectedCity.value;
      const maxParticipants = planMaxParticipants.trim() ? Number(planMaxParticipants) : 0;
      const hostName = user.displayName || 'Organizer';
      const chatId = `studizone-plan-${Date.now()}-${user.uid}`;
      const planData = {
        title: title.trim(),
        description: description.trim(),
        location: searchLocation.trim(),
        locationName: searchLocation.trim(),
        address: searchLocation.trim(),
        city: selectedCity.label,
        cityKey,
        date: startDate.trim(),
        time: startTime.trim(),
        startDate: startDate.trim(),
        startTime: startTime.trim(),
        displayTime: `${startDate.trim()} ${startTime.trim()}`,
        // "Plan ends at": the plan disappears from the app at endAt and the
        // daily cleanupExpiredPlans job deletes it after that.
        endDate,
        endTime,
        endAt: Timestamp.fromDate(endsAt),
        category: 'social',
        participants: 1,
        maxParticipants: maxParticipants || null,
        meetingType,
        visibility,
        safetyAcknowledged: true,
        hostId: user.uid,
        hostName,
        createdBy: user.uid,
        createdByRole: 'user',
        user: {
          id: user.uid,
          name: hostName,
          photo: user.photoURL || '',
        },
        isFree: true,
        requiresTicket: false,
        ticketPrice: '',
        price: '0',
        attendees: [],
        participantIds: [user.uid],
        adminIds: [user.uid],
        removedUserIds: [],
        approved: true,
        isPublic: visibility === 'public',
        entryType: 'community_plan',
        type: 'plan',
        ...(isStudiZonePlan ? {
          layer: 'studizone',
          source: 'studizone',
          isStudentOnly: true,
          visibility: 'student_only',
          audience: 'verified_students',
          zone: 'studyzone',
          adminId: user.uid,
          members: [user.uid],
          pendingRequests: [],
          approvedRequests: [],
          chatId,
        } : {}),
        sourceType: 'user_activity',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };
      // plans/{id} and planChats/{id} are created together in one batch —
      // previously two separate awaited calls, which could leave a plan
      // permanently missing its chat/membership doc (blocking join-request
      // accept forever) if anything interrupted the app between them.
      const planRef = doc(collection(firestore, FIRESTORE_COLLECTIONS.plans));
      const created = { id: planRef.id };
      const batch = writeBatch(firestore);
      batch.set(planRef, planData);
      batch.set(doc(firestore, FIRESTORE_COLLECTIONS.planChats, created.id), {
        chatType: isStudiZonePlan ? 'studizone_plan' : 'plan',
        layer: isStudiZonePlan ? 'studizone' : 'public',
        planId: created.id,
        chatId: isStudiZonePlan ? chatId : created.id,
        planTitle: title.trim(),
        planDescription: description.trim(),
        cityKey,
        members: [user.uid],
        memberIds: [user.uid],
        adminId: user.uid,
        organizerId: user.uid,
        adminIds: [user.uid],
        isStudentOnly: isStudiZonePlan,
        bannedUserIds: [],
        hiddenForUserIds: [],
        lastMessage: '',
        lastMessageAt: new Date().toISOString(),
        createdAt: new Date().toISOString(),
      });
      await batch.commit();

      if (isStudiZonePlan) {
        console.log('[StudiZonePlanCreate] created plan', {
          planId: created.id,
          layer: planData.layer,
          isStudentOnly: planData.isStudentOnly,
          chatId: planData.chatId,
        });
      }

      requestNotificationPermission().catch(error => {
        console.warn('[Tonyte][RN][Permissions] notification request failed', error);
      });
      if (isStudiZonePlan) {
        navigation.navigate('StudiZonePlansScreen', { initialTab: 'plans' });
      } else {
        navigation.goBack();
      }
    } catch (error) {
      Alert.alert(t('uploadPlan.publishFailed'), error instanceof Error ? error.message : t('common.tryAgain'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <TonyteBackground style={[styles.container, appearance.container]} appearanceAware>
      <ScrollView
        style={styles.scroll}
        contentContainerStyle={[styles.content, { paddingTop: insets.top + Spacing.sm, paddingBottom: insets.bottom + Spacing.xl }]}
        keyboardShouldPersistTaps="handled"
      >
        <View style={styles.header}>
          <TouchableOpacity onPress={() => navigation.goBack()}>
            <Text style={[styles.backText, appearance.backText]}>{t('uploadPlan.back')}</Text>
          </TouchableOpacity>
          <View>
            <Text style={[styles.kicker, appearance.kicker]}>{t('uploadPlan.communityPlan')}</Text>
            <Text style={[styles.headerTitle, appearance.textPrimary]}>{t('uploadPlan.createPlan')}</Text>
          </View>
          <View style={styles.headerSpacer} />
        </View>

        {templateValues ? (
          <Text style={[styles.templateHint, appearance.textMuted]}>{t('uploadPlan.startedFromTemplate')}</Text>
        ) : null}

        <View style={[styles.notice, appearance.notice]}>
          <Text style={[styles.noticeTitle, appearance.accentSecondaryText]}>{t('uploadPlan.alwaysFree')}</Text>
          <Text style={[styles.noticeText, appearance.textSecondary]}>{t('uploadPlan.alwaysFreeBody')}</Text>
        </View>

        <Field value={title} onChangeText={setTitle} placeholder={t('uploadPlan.title')} />
        <Field value={description} onChangeText={setDescription} placeholder={t('uploadPlan.description')} multiline />

        <Section title={t('uploadPlan.city')} detail={t('uploadPlan.cityHelp')}>
          {visibleCities.length > 1 ? (
            <View style={styles.chipGrid}>
              {visibleCities.map(option => (
                <TouchableOpacity
                  key={option.value}
                  style={[
                    styles.chip,
                    appearance.chip,
                    normalizeCityKey(city) === option.value && styles.chipActive,
                    normalizeCityKey(city) === option.value && appearance.chipActive,
                  ]}
                  onPress={() => setCity(option.label)}
                >
                  <Text style={[
                    styles.chipText,
                    appearance.chipText,
                    normalizeCityKey(city) === option.value && styles.chipTextActive,
                    normalizeCityKey(city) === option.value && appearance.chipTextActive,
                  ]}>
                    {option.label}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
          ) : (
            <Field value={defaultVisibleCity.label} editable={false} placeholder={t('uploadPlan.chooseCity')} noMargin />
          )}
        </Section>

        <Section title={t('uploadPlan.dateRange')} detail={t('uploadPlan.dateRangeHelp')}>
          <Label text={t('uploadPlan.location')} />
          <Field value={searchLocation} onChangeText={setSearchLocation} placeholder={t('uploadPlan.locationPlaceholder')} noMargin />
          <View style={styles.rowInputs}>
            <PickerField
              value={startDate}
              onChange={setStartDate}
              placeholder={t('uploadPlan.dateFrom')}
              mode="date"
              minimumDate={getTodayMidnight()}
              noMargin
            />
            <PickerField
              value={startTime}
              onChange={setStartTime}
              placeholder={t('uploadPlan.timeFrom')}
              mode="time"
              minimumDate={startDate === getTodayStr() ? new Date() : undefined}
              onReject={() => Alert.alert(t('uploadPlan.invalidTime'), t('uploadPlan.pastTime'))}
              noMargin
            />
          </View>
          <Label text={t('uploadPlan.endsAt')} />
          <Text style={[styles.inlineHelp, appearance.textMuted]}>{t('uploadPlan.endsAtHelp')}</Text>
          <View style={styles.rowInputs}>
            <PickerField
              value={endDate}
              onChange={value => { setEndTouched(true); setEndDate(value); }}
              placeholder={t('uploadPlan.endDate')}
              mode="date"
              minimumDate={startDate ? localDateTime(startDate, '00:00') ?? getTodayMidnight() : getTodayMidnight()}
              noMargin
            />
            <PickerField
              value={endTime}
              onChange={value => { setEndTouched(true); setEndTime(value); }}
              placeholder={t('uploadPlan.endTime')}
              mode="time"
              noMargin
            />
          </View>
        </Section>

        <Section title={t('uploadPlan.safety')} detail={t('uploadPlan.safetyHelp')}>
          <Label text={t('uploadPlan.hostedAt')} />
          <Text style={[styles.inlineHelp, appearance.textMuted]}>{t('uploadPlan.hostedHelp')}</Text>
          <View style={styles.chipGrid}>
            {MEETING_TYPES.map(option => (
              <TouchableOpacity
                key={option.value}
                style={[
                  styles.chip,
                  appearance.chip,
                  meetingType === option.value && styles.chipActive,
                  meetingType === option.value && appearance.chipActive,
                ]}
                onPress={() => setMeetingType(option.value)}
              >
                <Text style={[
                  styles.chipText,
                  appearance.chipText,
                  meetingType === option.value && styles.chipTextActive,
                  meetingType === option.value && appearance.chipTextActive,
                ]}>
                  {t(option.labelKey)}
                </Text>
              </TouchableOpacity>
            ))}
          </View>

          <Label text={t('uploadPlan.visibility')} />
          <Text style={[styles.inlineHelp, appearance.textMuted]}>{t('uploadPlan.visibilityHelp')}</Text>
          <View style={styles.chipGrid}>
            {VISIBILITY_OPTIONS.map(option => (
              <TouchableOpacity
                key={option.value}
                style={[
                  styles.chip,
                  appearance.chip,
                  visibility === option.value && styles.chipActive,
                  visibility === option.value && appearance.chipActive,
                ]}
                onPress={() => setVisibility(option.value)}
              >
                <Text style={[
                  styles.chipText,
                  appearance.chipText,
                  visibility === option.value && styles.chipTextActive,
                  visibility === option.value && appearance.chipTextActive,
                ]}>
                  {t(option.labelKey)}
                </Text>
              </TouchableOpacity>
            ))}
          </View>

          <Label text={t('uploadPlan.maxParticipants')} />
          <Field
            value={planMaxParticipants}
            onChangeText={setPlanMaxParticipants}
            placeholder={t('uploadPlan.unlimited')}
            keyboardType="numeric"
            noMargin
          />

          <View style={styles.switchRow}>
            <Text style={[styles.safetyText, appearance.textSecondary]}>
              {t('uploadPlan.responsibility')}
            </Text>
            <Switch
              value={safetyAcknowledged}
              onValueChange={setSafetyAcknowledged}
              trackColor={{ true: theme.colors.accentSecondary, false: theme.colors.borderStrong }}
              thumbColor="#fff"
            />
          </View>
        </Section>

        <TouchableOpacity
          style={[GlobalStyles.btnPrimary, styles.submitBtn, !safetyAcknowledged && styles.submitDisabled]}
          onPress={save}
          disabled={saving}
        >
          {saving ? <ActivityIndicator color="#fff" /> : <Text style={GlobalStyles.btnPrimaryText}>{t('uploadPlan.publish')}</Text>}
        </TouchableOpacity>
      </ScrollView>
    </TonyteBackground>
  );
};

const Label: React.FC<{ text: string }> = ({ text }) => (
  <ThemedLabel text={text} />
);

const ThemedLabel: React.FC<{ text: string }> = ({ text }) => {
  const { theme } = useTheme();
  return <Text style={[styles.label, daylightStyle(theme, { color: theme.colors.textSecondary })]}>{text}</Text>;
};

// Daylight cards are translucent; Android draws `elevation` under a
// translucent view as a solid rectangle inside the rounded card. So the soft
// card shadow is iOS-only — Android gets just the one rounded surface.
const cardShadow = (theme: TonyteTheme) => (Platform.OS === 'android' ? {} : theme.shadows.card);

const Field: React.FC<React.ComponentProps<typeof TextInput> & { noMargin?: boolean }> = ({
  noMargin,
  style,
  multiline,
  ...props
}) => {
  const { theme } = useTheme();
  const fieldTheme = daylightStyle(theme, {
    backgroundColor: theme.colors.inputBackground,
    borderColor: theme.colors.borderStrong,
    color: theme.colors.textPrimary,
    ...cardShadow(theme),
  });
  return (
    <View style={!noMargin && styles.fieldWrap}>
      <TextInput
        style={[GlobalStyles.inputBase, multiline && styles.textarea, fieldTheme, style]}
        placeholderTextColor={theme.colors.textMuted}
        multiline={multiline}
        numberOfLines={multiline ? 4 : 1}
        {...props}
      />
    </View>
  );
};

const Section: React.FC<{ title: string; detail?: string; children?: React.ReactNode }> = ({ title, detail, children }) => {
  const { theme } = useTheme();
  const appearance = useMemo(() => createThemeStyles(theme), [theme]);
  return (
    <View style={[styles.section, appearance.section]}>
      <Text style={[styles.sectionTitle, appearance.textPrimary]}>{title}</Text>
      {detail ? <Text style={[styles.sectionDetail, appearance.textMuted]}>{detail}</Text> : null}
      {children ? <View style={styles.sectionBody}>{children}</View> : null}
    </View>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: Colors.bg },
  scroll: { flex: 1, backgroundColor: 'transparent' },
  content: { paddingHorizontal: Spacing.base, gap: Spacing.sm },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: Spacing.md,
  },
  backText: { color: Colors.turquoise, fontSize: Typography.base },
  kicker: {
    alignSelf: 'center',
    borderRadius: Radius.pill,
    backgroundColor: 'rgba(38,208,206,0.15)',
    color: Colors.turquoise,
    fontSize: 10,
    fontWeight: Typography.bold,
    letterSpacing: 2,
    paddingHorizontal: 10,
    paddingVertical: 4,
    textTransform: 'uppercase',
  },
  headerTitle: {
    color: Colors.textPrimary,
    fontSize: Typography.xl,
    fontWeight: Typography.bold,
    marginTop: Spacing.xs,
    textAlign: 'center',
  },
  headerSpacer: { width: 60 },
  templateHint: {
    color: Colors.textMuted,
    textAlign: 'center',
    fontSize: Typography.xs,
    fontStyle: 'italic',
    marginBottom: Spacing.sm,
  },
  notice: {
    borderRadius: Radius.xl,
    borderWidth: 1,
    borderColor: 'rgba(38,208,206,0.25)',
    backgroundColor: 'rgba(38,208,206,0.1)',
    padding: Spacing.base,
  },
  noticeTitle: { color: Colors.turquoise, fontSize: Typography.sm, fontWeight: Typography.bold },
  noticeText: { color: Colors.textSecondary, fontSize: Typography.xs, marginTop: Spacing.xs },
  fieldWrap: { marginTop: Spacing.xs },
  textarea: { minHeight: 110, textAlignVertical: 'top' },
  section: {
    borderRadius: Radius.xl,
    borderWidth: 1,
    borderColor: Colors.border,
    backgroundColor: 'rgba(255,255,255,0.05)',
    padding: Spacing.base,
    marginTop: Spacing.sm,
    gap: Spacing.sm,
  },
  sectionTitle: {
    color: Colors.textPrimary,
    fontSize: Typography.sm,
    fontWeight: Typography.bold,
  },
  sectionDetail: {
    color: Colors.textMuted,
    fontSize: Typography.xs,
    lineHeight: 16,
  },
  sectionBody: { gap: Spacing.sm },
  label: {
    color: Colors.textSecondary,
    fontSize: Typography.sm,
    fontWeight: Typography.semibold,
    marginTop: Spacing.xs,
    marginBottom: Spacing.xs,
  },
  inlineHelp: {
    color: Colors.textMuted,
    fontSize: Typography.xs,
    lineHeight: 16,
    marginTop: -4,
  },
  rowInputs: { flexDirection: 'row', gap: Spacing.md, marginTop: Spacing.sm },
  chipGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: Spacing.sm },
  chip: {
    paddingHorizontal: Spacing.md,
    paddingVertical: Spacing.xs,
    borderRadius: Radius.pill,
    borderWidth: 1,
    borderColor: Colors.border,
    backgroundColor: Colors.bgInput,
  },
  chipActive: { borderColor: Colors.turquoise, backgroundColor: 'rgba(38,208,206,0.18)' },
  chipText: { color: Colors.textSecondary, fontSize: Typography.xs, fontWeight: Typography.semibold },
  chipTextActive: { color: Colors.turquoise },
  switchRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: Spacing.md,
    marginTop: Spacing.sm,
  },
  safetyText: { color: Colors.textSecondary, fontSize: Typography.xs, lineHeight: 18, flex: 1 },
  submitBtn: { marginTop: Spacing.xl },
  submitDisabled: { opacity: 0.7 },
});

const createThemeStyles = (theme: TonyteTheme) => StyleSheet.create({
  container: daylightStyle(theme, { backgroundColor: theme.colors.background }),
  backText: daylightStyle(theme, { color: theme.colors.accentSecondary }),
  kicker: daylightStyle(theme, {
    backgroundColor: theme.colors.accentSecondarySoft,
    color: theme.colors.accentSecondary,
    borderColor: theme.colors.accentSecondaryBorder,
    borderWidth: 1,
  }),
  notice: daylightStyle(theme, {
    backgroundColor: theme.colors.accentSecondarySoft,
    borderColor: theme.colors.accentSecondaryBorder,
    ...cardShadow(theme),
  }),
  section: daylightStyle(theme, {
    backgroundColor: theme.colors.cardBackground,
    borderColor: theme.colors.cardBorder,
    ...cardShadow(theme),
  }),
  chip: daylightStyle(theme, {
    backgroundColor: theme.colors.chipBackground,
    borderColor: theme.colors.borderStrong,
  }),
  chipActive: daylightStyle(theme, {
    backgroundColor: theme.colors.accentSecondarySoft,
    borderColor: theme.colors.accentSecondary,
  }),
  chipText: daylightStyle(theme, { color: theme.colors.textSecondary }),
  chipTextActive: daylightStyle(theme, { color: theme.colors.accentSecondary }),
  textPrimary: daylightStyle(theme, { color: theme.colors.textPrimary }),
  textSecondary: daylightStyle(theme, { color: theme.colors.textSecondary }),
  textMuted: daylightStyle(theme, { color: theme.colors.textMuted }),
  accentSecondaryText: daylightStyle(theme, { color: theme.colors.accentSecondary }),
});

export default UploadPlanScreen;
